import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Button, Box, Paper, Typography, IconButton, TextField, InputAdornment,
  Select, MenuItem, FormControl, InputLabel, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, useMediaQuery, useTheme, Stack, Grid, CircularProgress
} from '@mui/material';
import { Edit, DeleteOutline, Search, Icecream, Restore, Visibility } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, PRODUCTOS_LISTAR, PRODUCTO_ELIMINAR, PRODUCTO_REACTIVAR, PRODUCTO_GET } from '../services/apiConfig';
import { DataGrid } from '@mui/x-data-grid';
import Cargando from './utils/Cargando.jsx';
import TituloPagina from './utils/TituloPagina.jsx';
import ExportToolbar from './utils/ExportToolbar.jsx';
import axios from 'axios';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Los filtros se guardan en sessionStorage para que sigan puestos al volver
// de editar un producto (navegar a /producto y volver desmonta y remonta
// este componente entero, así que un simple useState los perdía).
const FILTROS_KEY = 'productos_filtros';
const cargarFiltrosGuardados = () => {
  try { return JSON.parse(sessionStorage.getItem(FILTROS_KEY) || '{}'); } catch { return {}; }
};
const fmtMoneda = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const esActivo = (p) => Number(p.activo) === 1;

function Thumb({ imagenUrl, size = 40 }) {
  const [err, setErr] = useState(false);
  const url = imagenUrl && !err ? `${API_BASE_URL}${imagenUrl}` : null;
  return (
    <Box sx={{ width: size, height: size, flexShrink: 0, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {url
        ? <img src={url} alt="" onError={() => setErr(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <Icecream sx={{ fontSize: Math.round(size * 0.55), color: 'primary.main', opacity: 0.6 }} />}
    </Box>
  );
}

// Popup de consulta: muestra el resumen de un producto y permite pasar a edición.
function ProductoDetalleDialog({ id, onClose, onEditar }) {
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    axios.get(API_BASE_URL + PRODUCTO_GET, { params: { id }, headers: authHeader() })
      .then((res) => setDetalle(res.data?.data ?? null))
      .catch((e) => setError(e.response?.data?.message || 'Error al cargar el detalle.'))
      .finally(() => setLoading(false));
  }, [id]);

  const activo = detalle && esActivo(detalle);

  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Detalle del producto #{id}
        {detalle && !activo && <Chip size="small" label="Baja" />}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
        {error && <Typography color="error" variant="body2">{error}</Typography>}
        {!loading && detalle && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Thumb imagenUrl={detalle.imagen_url} size={90} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{detalle.art_descripcion}</Typography>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 700, mb: 1 }}>{fmtMoneda(detalle.art_precio_normal)}</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">ID Club Grido</Typography><Typography variant="body2">{detalle.id_club_grido ?? '—'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Rubro</Typography><Typography variant="body2">{detalle.grudescrip || '—'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Costo</Typography><Typography variant="body2">{fmtMoneda(detalle.art_costo)}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Peso</Typography><Typography variant="body2">{detalle.art_peso ?? '—'} kg</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Descripción comanda</Typography><Typography variant="body2">{detalle.art_descripcion_ticket || '—'}</Typography></Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        {activo && !loading && (
          <Button variant="contained" startIcon={<Edit />} onClick={() => onEditar(id)}>Editar</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ProductosList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [productos, setProductos] = useState([]);
  const filtrosGuardados = useMemo(() => cargarFiltrosGuardados(), []);
  const [q, setQ] = useState(filtrosGuardados.q ?? '');
  const [qClub, setQClub] = useState(filtrosGuardados.qClub ?? '');
  const [grupoFiltro, setGrupoFiltro] = useState(filtrosGuardados.grupoFiltro ?? '');
  const [estado, setEstado] = useState(filtrosGuardados.estado ?? 'activos'); // activos | inactivos | todos
  const [eliminar, setEliminar] = useState(null);
  const [verId, setVerId] = useState(null);

  useEffect(() => {
    sessionStorage.setItem(FILTROS_KEY, JSON.stringify({ q, qClub, grupoFiltro, estado }));
  }, [q, qClub, grupoFiltro, estado]);

  const fetchProductos = useCallback(() => {
    setLoading(true);
    axios.get(API_BASE_URL + PRODUCTOS_LISTAR, { headers: authHeader() })
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setProductos(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setProductos([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProductos(); }, [fetchProductos]);

  const grupos = useMemo(() => {
    const m = new Map();
    productos.forEach((p) => { if (p.grupo != null && !m.has(p.grupo)) m.set(p.grupo, p.grudescrip || `Grupo ${p.grupo}`); });
    return Array.from(m, ([grupo, nombre]) => ({ grupo, nombre })).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [productos]);

  const filtered = useMemo(() => {
    const texto = q.trim().toLowerCase();
    const club = qClub.trim();
    return productos.filter((p) => {
      const okTexto = !texto || String(p.art_descripcion || '').toLowerCase().includes(texto);
      const okClub = !club || String(p.id_club_grido || '').includes(club);
      const okGrupo = grupoFiltro === '' || String(p.grupo) === String(grupoFiltro);
      const okEstado = estado === 'todos' || (estado === 'activos' ? esActivo(p) : !esActivo(p));
      return okTexto && okClub && okGrupo && okEstado;
    });
  }, [productos, q, qClub, grupoFiltro, estado]);

  const cambiarEstado = (url, id) => {
    axios.post(API_BASE_URL + url, { id }, { headers: authHeader() })
      .then(() => { setEliminar(null); fetchProductos(); })
      .catch(() => setEliminar(null));
  };
  const reactivar = (p) => cambiarEstado(PRODUCTO_REACTIVAR, p.id);

  const columns = [
    {
      field: 'imagen', headerName: '', width: 60, sortable: false, filterable: false,
      renderCell: (p) => <Thumb imagenUrl={p.row.imagen_url} size={38} />,
    },
    {
      field: 'art_descripcion', headerName: 'Descripción', flex: 1, minWidth: 200,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>{p.row.art_descripcion}</span>
          {!esActivo(p.row) && <Chip size="small" color="default" label="Baja" sx={{ height: 20 }} />}
        </Box>
      ),
    },
    { field: 'id_club_grido', headerName: 'ID Club Grido', width: 130 },
    { field: 'grudescrip', headerName: 'Rubro', width: 150 },
    {
      field: 'art_peso', headerName: 'Peso', width: 100, align: 'right', headerAlign: 'right',
      renderCell: (p) => Number(p.row.art_peso) > 0 ? `${Number(p.row.art_peso).toFixed(3)} kg` : '—',
    },
    {
      field: 'art_precio_normal', headerName: 'Precio', width: 130, align: 'right', headerAlign: 'right',
      renderCell: (p) => <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtMoneda(p.row.art_precio_normal)}</Typography>,
    },
    {
      field: 'actions', headerName: '', width: 130, sortable: false, filterable: false, align: 'right',
      renderCell: (p) => esActivo(p.row) ? (
        <>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setVerId(p.row.id); }}><Visibility fontSize="small" /></IconButton>
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/producto?id=${p.row.id}`); }}><Edit fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setEliminar(p.row); }}><DeleteOutline fontSize="small" /></IconButton>
        </>
      ) : (
        <IconButton size="small" color="success" title="Reactivar" onClick={(e) => { e.stopPropagation(); reactivar(p.row); }}><Restore fontSize="small" /></IconButton>
      ),
    },
  ];

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={Icecream}
          titulo="Productos"
          subtitulo={`${filtered.length} de ${productos.length} producto${productos.length !== 1 ? 's' : ''}`}
          ayuda="Para cambiar muchos precios a la vez (ej. lista nueva de Grido), usá 'Actualizar precios' en vez de editar producto por producto."
          accion={
            <Button variant="contained" color="primary" onClick={() => navigate('/producto?mode=create')}>
              Nuevo producto
            </Button>
          }
        />

        {/* Filtros en vivo */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Buscar por descripción…"
            value={q} onChange={(e) => setQ(e.target.value)}
            sx={{ flex: '1 1 240px', maxWidth: 360 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          <TextField
            size="small" placeholder="ID Club Grido…"
            value={qClub} onChange={(e) => setQClub(e.target.value)}
            sx={{ width: 170 }}
          />
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Rubro</InputLabel>
            <Select label="Rubro" value={grupoFiltro} onChange={(e) => setGrupoFiltro(e.target.value)}>
              <MenuItem value=""><em>Todos los rubros</em></MenuItem>
              {grupos.map((g) => <MenuItem key={g.grupo} value={g.grupo}>{g.nombre}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Estado</InputLabel>
            <Select label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)}>
              <MenuItem value="activos">Activos</MenuItem>
              <MenuItem value="inactivos">Dados de baja</MenuItem>
              <MenuItem value="todos">Todos</MenuItem>
            </Select>
          </FormControl>
          {(q || qClub || grupoFiltro !== '' || estado !== 'activos') && (
            <Button size="small" onClick={() => { setQ(''); setQClub(''); setGrupoFiltro(''); setEstado('activos'); }}>Limpiar</Button>
          )}
        </Box>

        {loading ? (
          <Cargando />
        ) : isMobile ? (
          <Stack spacing={1}>
            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Sin resultados.</Typography>
            )}
            {filtered.map((p) => {
              const activo = esActivo(p);
              return (
                <Paper key={p.id} variant="outlined"
                  onClick={() => setVerId(p.id)}
                  sx={{ p: 1.2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', opacity: activo ? 1 : 0.6, '&:active': { bgcolor: 'action.hover' } }}>
                  <Thumb imagenUrl={p.imagen_url} size={44} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.1 }}>{p.art_descripcion}</Typography>
                      {!activo && <Chip size="small" label="Baja" sx={{ height: 18 }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Club {p.id_club_grido ?? '—'} · {p.grudescrip || '—'}
                      {Number(p.art_peso) > 0 ? ` · ${Number(p.art_peso).toFixed(3)} kg` : ''}
                    </Typography>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>{fmtMoneda(p.art_precio_normal)}</Typography>
                  </Box>
                  {activo ? (
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setEliminar(p); }}><DeleteOutline fontSize="small" /></IconButton>
                  ) : (
                    <Button size="small" color="success" startIcon={<Restore />} onClick={(e) => { e.stopPropagation(); reactivar(p); }}>Reactivar</Button>
                  )}
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <div style={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={filtered} columns={columns} pageSize={10} rowsPerPageOptions={[10, 25, 50]}
              getRowId={(r) => r.id} disableSelectionOnClick rowHeight={52}
              onRowClick={(params) => setVerId(params.row.id)}
              sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
              components={{ Toolbar: ExportToolbar }}
            />
          </div>
        )}
      </Box>

      <Dialog open={!!eliminar} onClose={() => setEliminar(null)}>
        <DialogTitle>Dar de baja producto</DialogTitle>
        <DialogContent>
          <Typography variant="body2">¿Dar de baja <strong>{eliminar?.art_descripcion}</strong>? Deja de aparecer en el catálogo y en la carga de registros. Podés reactivarlo después desde el filtro "Dados de baja".</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEliminar(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => cambiarEstado(PRODUCTO_ELIMINAR, eliminar.id)}>Dar de baja</Button>
        </DialogActions>
      </Dialog>

      {verId && (
        <ProductoDetalleDialog
          id={verId}
          onClose={() => setVerId(null)}
          onEditar={(id) => navigate(`/producto?id=${id}`)}
        />
      )}
    </Container>
  );
}

export default ProductosList;
